import {
	getAssureReductionsUrl,
	getPlantOverviewUrl,
} from "@pages/mrv/lib/urls"
import type { UserPermission } from "@shared/api/authentication/types"
import { PermissionType } from "@shared/api/authentication/types"
import { materials, outbox } from "@shared/routes"
import { unauthorized } from "@shared/routes/technical"

/**
 * Determines the default route for a user based on their permissions.
 * The order of checks represents the priority of permissions.
 *
 * Priority order:
 * 1. MrvAnnualAssurer → /carbon-savings/plants
 * 2. MrvCoordinator / MrvDepositAssurer → /carbon-savings/assure-reductions
 * 3. Customer → /materials
 * 4. Admin / Developer → /outbox
 * 5. No permissions or unknown → /unauthorized
 *
 * @param permissions - Array of user permissions
 * @returns The route path to redirect to
 */
export const getDefaultRouteByPermissions = (
	permissions: UserPermission[] | undefined,
): string => {
	if (!permissions || permissions.length === 0) {
		return `/${unauthorized}`
	}

	const permissionTypes = new Set(permissions.map(p => p.permissionType))

	if (permissionTypes.has(PermissionType.MrvAnnualAssurer)) {
		return getPlantOverviewUrl()
	}

	if (
		permissionTypes.has(PermissionType.MrvCoordinator) ||
		permissionTypes.has(PermissionType.MrvDepositAssurer)
	) {
		return getAssureReductionsUrl()
	}

	if (
		permissionTypes.has(PermissionType.Customer) ||
		permissionTypes.has(PermissionType.SalesRepresentative)
	) {
		return materials
	}

	if (
		permissionTypes.has(PermissionType.Admin) ||
		permissionTypes.has(PermissionType.Developer)
	) {
		return outbox
	}

	return `/${unauthorized}`
}
